/**
 * Trip Planner Application - Main Entry Point
 * 
 * This file serves as the central entry point and routing configuration for the Trip Planner application.
 * It defines the routing structure, authentication protection, and application layout integration.
 * 
 * Key responsibilities:
 * - Setting up route definitions
 * - Implementing authentication protection for routes
 * - Providing context providers (Auth, Trip)
 * - Managing entity type detection for breadcrumb enhancement
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import {
  AuthProvider,
  useAuth,
  TripProvider,
  initializePlacesService,
  WaitingIndicator,
  initConnectionRegistryServices,
  auth,
  linkExtractorService,
  connectionRegistryService,
  linkRegistryConfigService,
  tripLinkAuditService,
  entityLinkingService
} from '@trip-planner-monorepo/shared';
import AppLayout from './components/layout/AppLayout';
import Login from './components/Login';
import TripsList from './components/TripsList';
import { DaysList, DayDetailPage, initDaysServices } from '@trip-planner-monorepo/days';
import {
  Places,
  PlaceDetails,
  initPlacesServices
} from '@trip-planner-monorepo/places';
import { TravelList, TravelDetails, initTravelServices } from '@trip-planner-monorepo/travel';
import ReactModal from 'react-modal';
import {
  AccommodationList,
  AccommodationDetails,
  initAccommodationServices
} from '@trip-planner-monorepo/accommodation';
import { ExtrasPage } from '@trip-planner-monorepo/extras';
import { suggestionService, GenericSuggestionPopup } from '@trip-planner-monorepo/suggestions';
import {
  DocumentsPage,
  DocumentDetailsPage,
  initDocumentsServices,
  documentService
} from '@trip-planner-monorepo/documents';
import { initAppFeaturesServices, TravelAgentPage } from '@trip-planner-monorepo/app-features';
import AdminCleanup from './components/AdminCleanup';

// Initialize cross-entity services once at module level
// CRITICAL: Services must be initialized HERE, not in package index.js files
// See: Documentation/Service-Initialization-Anti-Patterns.md
let servicesInitialized = false;
const initializeServices = async () => {
  if (!servicesInitialized) {
    initDaysServices();
    initPlacesServices();
    initAccommodationServices();
    initTravelServices();
    initDocumentsServices();
    initConnectionRegistryServices();
    suggestionService.init();
    await initAppFeaturesServices();
    servicesInitialized = true;
    console.log('All cross-entity services initialized');
  }
};

// Initialize services immediately but only once
initializeServices();

// Dev-only: expose registry tools on window for migration console scripts
if (process.env.NODE_ENV === 'development') {
  window.__tripPlanner = {
    auth,
    linkExtractorService,
    connectionRegistryService,
    linkRegistryConfigService,
    tripLinkAuditService,
    entityLinkingService,
    documentService
  };
}

// Note: Removed complex accommodationDateChangeDetector - now using simplified approach

/**
 * Initialize the Google Places API Service
 * This makes the Places API available throughout the application
 * Using the API key from environment variables
 */
try {
  // Debug: Log all environment variables that start with REACT_APP_
  console.log('All REACT_APP_ environment variables:',
    Object.keys(process.env)
      .filter(key => key.startsWith('REACT_APP_'))
      .reduce((obj, key) => {
        // Only show first few characters of potential API keys for security
        const value = process.env[key];
        obj[key] = typeof value === 'string' && value.length > 10
          ? `${value.substring(0, 4)}...`
          : value;
        return obj;
      }, {})
  );

  // Log the environment variable value (without revealing the full key in production)
  const apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

  // For testing purposes only - remove or replace with your actual key for production
  // This is a fallback if the .env file is not being read correctly
  // const testApiKey = "your_actual_api_key_here";

  if (!apiKey) {
    console.error('Google Places API key is missing from environment variables.');
    console.error('Please check that your .env file exists in the correct location:');
    console.error('- packages/main-app/.env');
    console.error('- OR at the root of your project');
    console.error('And contains: REACT_APP_GOOGLE_PLACES_API_KEY=your_api_key');

    // Uncomment the line below and add your key for testing
    // initializePlacesService({ apiKey: testApiKey });
    // console.log('Using test API key as fallback');
  } else {
    // For debugging, show the first few characters of the key
    console.log(`Found Google Places API key starting with: ${apiKey.substring(0, 4)}...`);

    // Initialize the service
    initializePlacesService();
    console.log('Google Places API service initialized successfully');
  }
} catch (error) {
  console.error('Failed to initialize Google Places API service:', error);
}

/**
 * Configure React Modal
 * This sets the root element for the modal library to ensure accessibility
 * It's a required step for react-modal to function properly
 */
ReactModal.setAppElement('#root');

/**
 * PrivateRoute Component
 * 
 * PURPOSE:
 * Protects routes that require authentication by redirecting unauthenticated users to the login page.
 * 
 * HOW IT WORKS:
 * 1. Consumes the auth context to check if a user is logged in
 * 2. Shows a loading indicator while auth state is being determined
 * 3. Renders the children components if authenticated
 * 4. Redirects to login page if not authenticated
 * 
 * @param {React.ReactNode} children - The components to render if authenticated
 */
function PrivateRoute({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) {
    return <WaitingIndicator fullPage={true} message="Preparing your journey..." />;
  }
  return currentUser ? children : <Navigate to="/login" />;
}

/**
 * Main App Component
 * 
 * PURPOSE:
 * Serves as the application's root component, setting up the router and authentication provider.
 * 
 * STRUCTURE:
 * 1. Router - Provides routing capabilities to the entire application
 * 2. AuthProvider - Provides authentication state and functions
 * 3. AppContent - Contains the actual routes and trip provider
 */
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

/**
 * AppContent Component
 * 
 * PURPOSE:
 * Contains the main application content and routes protected by authentication.
 * Manages entity type detection for breadcrumb enhancement.
 * 
 * DESIGN PATTERN:
 * This component is separated from App to use React Router hooks (useLocation)
 * which must be used within Router context.
 */
function AppContent() {
  /**
   * Authentication Context
   * 
   * Access the current authentication state
   * Includes a safety check to handle the context potentially being unavailable
   */
  const auth = useAuth() || {};
  const { currentUser } = auth;

  /**
   * Route Configuration
   * 
   * PURPOSE:
   * Defines the application's routing structure with authentication protection
   * 
   * PATTERN:
   * Most routes follow a consistent pattern:
   * 1. Path definition
   * 2. PrivateRoute for authentication protection
   * 3. AppLayout wrapper for consistent UI
   * 4. Page-specific component as the content
   * 
   * ORGANIZATION:
   * Routes are grouped by feature area (trips, places, travel, accommodation, etc.)
   */
  return (
    <TripProvider>
      <Routes>
        {/* Public route - Login page */}
        <Route path="/login" element={<Login />} />

        {/* Home route - Trip list */}
        <Route path="/" element={
          <PrivateRoute>
            <AppLayout>
              <TripsList />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Trip routes - Days view (main trip view) */}
        <Route path="/trips/:tripId" element={ // Main view for a trip, listing all its days
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <DaysList />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Day Detail Page Route - Displays details for a specific day */}
        <Route path="/trips/:tripId/day/:dayId" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <DayDetailPage />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Places routes - List and detail views */}
        <Route path="/trips/:tripId/places" element={ // Lists places associated with a trip
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <Places />
            </AppLayout>
          </PrivateRoute>
        } />
        <Route path="/trips/:tripId/places/:placeId" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <PlaceDetails />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Travel routes - List and detail views */}
        <Route path="/trips/:tripId/travel" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <TravelList />
            </AppLayout>
          </PrivateRoute>
        } />
        <Route path="/trips/:tripId/travel/:travelId" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <TravelDetails />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Accommodation routes - List and detail views */}
        <Route path="/trips/:tripId/accommodation" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <AccommodationList />
            </AppLayout>
          </PrivateRoute>
        } />
        <Route path="/trips/:tripId/accommodation/:accommodationId" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <AccommodationDetails />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Extras routes */}
        <Route path="/trips/:tripId/extras" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <ExtrasPage />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Documents routes */}
        <Route path="/trips/:tripId/documents" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <DocumentsPage />
            </AppLayout>
          </PrivateRoute>
        } />
        <Route path="/trips/:tripId/documents/:documentId" element={
          <PrivateRoute>
            <AppLayout showSearch={true}>
              <DocumentDetailsPage />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Travel Agent route — fullHeight disables main-content scroll so the
            three-column chat layout fills the viewport correctly */}
        <Route path="/travel-agent" element={
          <PrivateRoute>
            <AppLayout showSearch={false} fullHeight={true}>
              <TravelAgentPage />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Admin routes - Cleanup utility */}
        <Route path="/admin-cleanup" element={
          <PrivateRoute>
            <AppLayout showSearch={false}>
              <AdminCleanup />
            </AppLayout>
          </PrivateRoute>
        } />

        {/* Fallback route - Redirects unknown paths to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global components - shown regardless of route */}
      {/* TEMPORARILY DISABLED: Old PlaceSuggestionPopup to prevent conflicts with new GenericSuggestionPopup */}
      {/* TODO: Remove this component entirely once new system is fully tested and working */}
      {/* {currentUser && <PlaceSuggestionPopup />} */}
      {currentUser && <GenericSuggestionPopup />}
    </TripProvider>
  );
}

export default App;